import { useState, useEffect } from 'react';
import { useErrorBoundary } from 'react-error-boundary';

import Grid from '@mui/material/Grid2';
import Main from '../../layouts/Main';
import Container from '../../components/Container';
import {Card, Divider, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import PageBreadcrumbs from '../../components/PageBreadcrumbs';

import FullPublicFiler from '../../types/filers/fullPublicFiler';
import FilerTag from '../../types/filers/filerTag';
import { getApiData } from '../../utility/getApiData';

const BarredFilers = (): JSX.Element => {

    const { showBoundary } = useErrorBoundary();  
    const [barredFilers, setBarredFilers] = useState<FullPublicFiler[]>();

    document.title = 'OCPF - Ballot Disqualification';

    useEffect(() => {
        getBarredFilers();
    }, []);

    const getBarredFilers = async () => {
        let url = `${process.env.REACT_APP_API_BASE_URL}/filers/barred`;
        const { data, error } = await getApiData(url);
        if (error) {
            showBoundary(new Error('Failed to get the barred filers.'));
            return;
        }
        setBarredFilers(data);
    }

    const getDisqualifiedYear = (tags:FilerTag[]) => {
        const disqualifiedTag = tags.find(tag => tag.tagTypeId === 20);
        return disqualifiedTag ? disqualifiedTag.year : null;
    }

  return (
    <Main>
      <Container>
        <PageBreadcrumbs></PageBreadcrumbs>
        <Typography
            variant="h5"
            align={'left'}
            sx={{
                fontWeight: 700,
            }}
            paddingTop={3}
            gutterBottom
            marginBottom={2}
            >
            Ballot Disqualification
        </Typography>
        <Divider/>
        <Grid container marginTop={3}>
            <Grid 
              padding={3}
              size={12}
              component={Card}
              >
              <Typography variant='subtitle1' fontWeight={550} marginBottom={2}>Candidates disqualified from appearing on any municipal preliminary, state primary or general or special election ballot</Typography>
              <Typography component={'p'} variant='body1'  marginBottom={2}>
                The name of a candidate who is required to file campaign finance reports with the director, 
                and who fails to file any statement or report after the initiation of civil proceedings to compel the filing, 
                shall not be printed on any municipal preliminary, 
                state primary or general or special election ballot unless the statement or report is timely filed pursuant to chapter 53 or any charter or special law establishing the filing deadline. 
              </Typography>
              <Typography component={'p'} variant='body1'  marginBottom={2}>
                The director shall notify the state secretary or, for municipal candidates, 
                the registrars of the city or town, of the names of those candidates against whom civil proceedings for failure to timely file have been initiated and shall do so within 72 hours of the filing deadline for nomination papers for the candidate.
              </Typography>
              <Typography component={'p'} variant='body1'  marginBottom={2}>
                Any candidate who is disqualified from appearing on any municipal preliminary, 
                state primary or general or special election ballot as set forth above shall be ineligible to be nominated or elected as a write-in or sticker candidate unless the candidate shall have filed the statements or reports which are the subject of the civil litigation by the date of the municipal preliminary, state primary or general or special election in which the candidate is seeking nomination or election. If civil proceedings are initiated against a state or county candidate for failure to timely file the campaign finance report or statement and the candidate files the report or statement before the primary, the director shall notify the state secretary not later than 24 hours after the date of the primary. If civil proceedings are initiated against a municipal candidate who is required to file with the director and the candidate files the campaign finance report or statement prior to the preliminary or, if no preliminary is held, prior to the date the preliminary for the office sought would have been held, the director shall notify the registrars not later than 24 hours after the date of the preliminary or the date the preliminary would have been held.
              </Typography>
              <Typography component={'p'} variant='body1'  marginBottom={2}>
                  Please contact OCPF by <Link underline='none' href={'mailto:ocpf@mass.gov'}>e-mail</Link>, or call 617-979-8300 should you have any questions on the information here.
              </Typography>
            </Grid>
            <Grid 
                size={12}
                padding={3}
                marginTop={2}
                >
                <Typography variant='subtitle1' fontWeight={550} marginTop={3} marginBottom={2}>Barred Filers</Typography>
                {barredFilers && barredFilers.length > 0 ? (
                    <TableContainer>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><Typography variant='subtitle2' fontWeight={550}>CPF ID</Typography></TableCell>
                                    <TableCell><Typography variant='subtitle2' fontWeight={550}>Name/Address</Typography></TableCell>
                                    <TableCell><Typography variant='subtitle2' fontWeight={550}>Disqualified Since</Typography></TableCell>
                                    <TableCell><Typography variant='subtitle2' fontWeight={550}>Document</Typography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                            {barredFilers.map((filer) =>
                                <TableRow
                                    key={filer.cpfId}
                                >
                                    <TableCell><Link href={`/filers?q=${filer.cpfId}`} target="_blank" underline='none'>{filer.cpfId}</Link></TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {filer.fullNameReverse}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            <i>{filer.candidate.fullAddress}</i>
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" className="issue-link-text">{getDisqualifiedYear(filer.tags)}</Typography>
                                    </TableCell>
                                    <TableCell className="is-clickable">
                                        <Link href={`https://ocpf2.blob.core.windows.net/pdf/filers/${filer.cpfId}/${filer.documentFileName}`} target="_blank" underline='none'>File Link</Link>
                                    </TableCell>
                                </TableRow>
                        )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography variant='body1'>No barred filers found.</Typography>
                )}

            </Grid>
        </Grid>
      </Container>
    </Main>
  );
};

export default BarredFilers;